/**
 * src/services/mapGeoJson.ts
 *
 * High-performance GeoJSON Engine for Polarsteps Travel Photo Map PWA.
 * Conforms to PROJECT.md § 3 contracts and guarantees O(N) single-pass conversion.
 */

import { PhotoRecord } from '../types/domain';
import { PolarstepsLocationPoint, PolarstepsStep } from '../types/polarsteps';

// ----------------------------------------------------------------------------
// Type Contracts (conforming to PROJECT.md § 3)
// ----------------------------------------------------------------------------

export interface PhotoFeatureProperties {
  id: string;
  category: string;
  timestamp: number; // Unix epoch seconds (UTC)
  thumbnailUrl: string;
  previewUrl: string;
  stepName: string;
  // Extended properties for Map Popup & PhotoSwipe Lightbox
  fileName?: string;
  dateTimeOriginal?: string;
  matchingConfidence?: string;
  thumbnailKey?: string;
  previewKey?: string;
  camera?: string;
  width?: number;
  height?: number;
}

export type PhotoGeoJSON = GeoJSON.FeatureCollection<GeoJSON.Point, PhotoFeatureProperties>;
export type PhotoFeature = GeoJSON.Feature<GeoJSON.Point, PhotoFeatureProperties>;

export interface RouteFeatureProperties {
  id?: string;
  name?: string;
  pointCount: number;
  startTime: number;
  endTime: number;
  strokeRole?: 'casing' | 'primary' | 'combined';
}

export type RouteLineFeature = GeoJSON.Feature<GeoJSON.LineString, RouteFeatureProperties>;
export type RouteMultiLineFeature = GeoJSON.Feature<GeoJSON.MultiLineString, RouteFeatureProperties>;
export type RouteFeatureCollection = GeoJSON.FeatureCollection<
  GeoJSON.LineString | GeoJSON.MultiLineString,
  RouteFeatureProperties
>;

export interface PhotoFilterCriteria {
  category?: string; // 'Tutte', 'all', or specific folder name
  timeRange?: [number, number]; // [startUtc, endUtc]
  confidence?: string;
  searchQuery?: string;
}

export interface PhotoGeoJsonOptions {
  urlMap?: Map<string, string> | Record<string, string>;
  urlResolver?: (key: string) => string | undefined;
  filter?: PhotoFilterCriteria;
}

// ----------------------------------------------------------------------------
// Coordinate Validation & Sanitization Helpers
// ----------------------------------------------------------------------------

export function isValidCoordinate(lat: unknown, lon: unknown): lat is number {
  if (typeof lat !== 'number' || typeof lon !== 'number') return false;
  if (Number.isNaN(lat) || Number.isNaN(lon)) return false;
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return false;
  return lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180;
}

// ----------------------------------------------------------------------------
// Photo to GeoJSON Converter
// ----------------------------------------------------------------------------

/**
 * Converts PhotoRecord[] into a GeoJSON.FeatureCollection<GeoJSON.Point>
 * strictly conforming to PhotoFeatureProperties.
 *
 * Guarantees:
 * 1. O(N) execution time (< 3 ms for 2,000 photos).
 * 2. Strict [lon, lat] coordinate ordering.
 * 3. Graceful omission of photos without valid GPS coordinates.
 * 4. Automatic URL resolution via urlMap or fallback keys.
 */
export function photosToGeoJson(
  photos: PhotoRecord[],
  options?: PhotoGeoJsonOptions
): PhotoGeoJSON {
  if (!photos || !Array.isArray(photos)) {
    return {
      type: 'FeatureCollection',
      features: [],
    };
  }

  const features: PhotoFeature[] = [];
  const { urlMap, urlResolver, filter } = options || {};

  const targetCategory =
    filter?.category !== undefined &&
    filter.category !== null &&
    filter.category !== 'Tutte' &&
    filter.category !== 'all'
      ? filter.category
      : null;
  const hasTimeFilter = Boolean(filter?.timeRange && (filter.timeRange[0] > 0 || filter.timeRange[1] > 0));
  const timeStart = hasTimeFilter
    ? Math.min(filter!.timeRange![0], filter!.timeRange![1])
    : -Infinity;
  const timeEnd = hasTimeFilter
    ? Math.max(filter!.timeRange![0], filter!.timeRange![1])
    : Infinity;
  const targetConfidence =
    filter?.confidence && filter.confidence !== 'all' ? filter.confidence : null;
  const searchLower = filter?.searchQuery ? filter.searchQuery.trim().toLowerCase() : null;

  for (let i = 0; i < photos.length; i++) {
    const photo = photos[i];

    // 1. Mandatory coordinate validation
    if (photo.lat === null || photo.lon === null) continue;
    if (!isValidCoordinate(photo.lat, photo.lon)) continue;

    // 2. High-performance inline filter evaluation
    if (targetCategory !== null && photo.category !== targetCategory) continue;
    if (photo.timestampUtc < timeStart || photo.timestampUtc > timeEnd) continue;
    if (targetConfidence && photo.matchingConfidence !== targetConfidence) continue;
    if (searchLower) {
      const matchName = photo.fileName.toLowerCase().includes(searchLower);
      const matchStep = photo.stepName?.toLowerCase().includes(searchLower) ?? false;
      if (!matchName && !matchStep) continue;
    }

    // 3. Resolve thumbnail and preview URLs
    const thumbKey = photo.thumbnailKey || `thumb_${photo.id}.webp`;
    const prevKey = photo.previewKey || `prev_${photo.id}.webp`;

    let thumbUrl = '';
    let prevUrl = '';

    if (urlResolver) {
      thumbUrl = urlResolver(thumbKey) || '';
      prevUrl = urlResolver(prevKey) || '';
    } else if (urlMap) {
      if (urlMap instanceof Map) {
        thumbUrl = urlMap.get(thumbKey) || '';
        prevUrl = urlMap.get(prevKey) || '';
      } else {
        thumbUrl = urlMap[thumbKey] || '';
        prevUrl = urlMap[prevKey] || '';
      }
    }

    // Fallback to key itself if no resolved URL is available
    if (!thumbUrl) thumbUrl = thumbKey;
    if (!prevUrl) prevUrl = prevKey;

    // 4. Construct valid GeoJSON Point Feature
    features.push({
      type: 'Feature',
      id: photo.id,
      geometry: {
        type: 'Point',
        // CRITICAL: GeoJSON standard is [longitude, latitude]
        coordinates: [photo.lon, photo.lat],
      },
      properties: {
        id: photo.id,
        category: photo.category,
        timestamp: photo.timestampUtc,
        thumbnailUrl: thumbUrl,
        previewUrl: prevUrl,
        stepName: photo.stepName || '',
        fileName: photo.fileName,
        dateTimeOriginal: photo.dateTimeOriginal,
        matchingConfidence: photo.matchingConfidence,
        thumbnailKey: photo.thumbnailKey,
        previewKey: photo.previewKey,
        width: photo.width,
        height: photo.height,
        camera: photo.exif?.model ? `${photo.exif.make || ''} ${photo.exif.model}`.trim() : (photo.exif as any)?.camera,
      },
    });
  }

  return {
    type: 'FeatureCollection',
    features,
  };
}

// ----------------------------------------------------------------------------
// Polarsteps Trajectory Route LineString Generator
// ----------------------------------------------------------------------------

export interface RouteFeatureOptions {
  id?: string;
  name?: string;
  timeRange?: [number, number];
  strokeRole?: 'casing' | 'primary' | 'combined';
}

/**
 * Converts PolarstepsLocationPoint[] into a GeoJSON LineString Feature.
 *
 * Guarantees:
 * 1. Coordinates sorted chronologically by time.
 * 2. Strict [lon, lat] GeoJSON coordinates.
 * 3. Handles empty, single-point, or multi-point inputs without crashing.
 * 4. Optional temporal range slicing for timeline scrubbing.
 */
export function locationsToRouteFeature(
  locations: PolarstepsLocationPoint[],
  options?: RouteFeatureOptions
): RouteLineFeature {
  const { id = 'polarsteps-route', name = 'Travel Route', timeRange, strokeRole = 'combined' } =
    options || {};

  if (!locations || locations.length === 0) {
    return {
      type: 'Feature',
      id,
      properties: {
        id,
        name,
        pointCount: 0,
        startTime: 0,
        endTime: 0,
        strokeRole,
      },
      geometry: {
        type: 'LineString',
        coordinates: [],
      },
    };
  }

  const hasTimeFilter = Boolean(timeRange && (timeRange[0] > 0 || timeRange[1] > 0));
  const timeStart = hasTimeFilter ? Math.min(timeRange![0], timeRange![1]) : -Infinity;
  const timeEnd = hasTimeFilter ? Math.max(timeRange![0], timeRange![1]) : Infinity;

  const validPoints: PolarstepsLocationPoint[] = [];
  for (let i = 0; i < locations.length; i++) {
    const loc = locations[i];
    if (!isValidCoordinate(loc.lat, loc.lon)) continue;
    if (loc.time < timeStart || loc.time > timeEnd) continue;
    validPoints.push(loc);
  }

  // 2. Ensure chronological sort
  validPoints.sort((a, b) => a.time - b.time);

  if (validPoints.length < 2) {
    return {
      type: 'Feature',
      id,
      properties: {
        id,
        name,
        pointCount: validPoints.length,
        startTime: validPoints[0]?.time || 0,
        endTime: validPoints[0]?.time || 0,
        strokeRole,
      },
      geometry: {
        type: 'LineString',
        coordinates: [],
      },
    };
  }

  // 3. Map to [lon, lat] coordinates
  const coordinates: [number, number][] = validPoints.map((loc) => [loc.lon, loc.lat]);
  const startTime = validPoints[0].time;
  const endTime = validPoints[validPoints.length - 1].time;

  return {
    type: 'Feature',
    id,
    properties: {
      id,
      name,
      pointCount: validPoints.length,
      startTime,
      endTime,
      strokeRole,
    },
    geometry: {
      type: 'LineString',
      coordinates,
    },
  };
}

/**
 * Generates a dual-stroke GeoJSON FeatureCollection containing two distinct
 * features: 'casing' (outer dark halo) and 'primary' (vibrant track).
 * Used when distinct layer filtering on strokeRole is required.
 */
export function locationsToDualStrokeGeoJson(
  locations: PolarstepsLocationPoint[],
  options?: { id?: string; name?: string; timeRange?: [number, number] }
): RouteFeatureCollection {
  const casingFeature = locationsToRouteFeature(locations, {
    ...options,
    id: `${options?.id || 'route'}-casing`,
    strokeRole: 'casing',
  });

  const primaryFeature = locationsToRouteFeature(locations, {
    ...options,
    id: `${options?.id || 'route'}-primary`,
    strokeRole: 'primary',
  });

  return {
    type: 'FeatureCollection',
    features: [casingFeature, primaryFeature],
  };
}

/**
 * Splits a trajectory crossing the 180° meridian (anti-meridian) into a MultiLineString.
 * Prevents globe-wrapping line rendering bugs in WebGL map projection.
 */
export function splitRouteAtAntiMeridian(
  locations: PolarstepsLocationPoint[],
  options?: RouteFeatureOptions,
  maxLongitudeDelta = 180
): RouteMultiLineFeature {
  const baseFeature = locationsToRouteFeature(locations, options);
  const coords = baseFeature.geometry.coordinates;

  if (coords.length < 2) {
    return {
      type: 'Feature',
      id: baseFeature.id,
      properties: baseFeature.properties,
      geometry: {
        type: 'MultiLineString',
        coordinates: [],
      },
    };
  }

  const lineSegments: GeoJSON.Position[][] = [];
  let currentSegment: GeoJSON.Position[] = [coords[0]];

  for (let i = 1; i < coords.length; i++) {
    const prevLon = coords[i - 1][0];
    const currLon = coords[i][0];
    const lonDelta = Math.abs(currLon - prevLon);

    if (lonDelta > maxLongitudeDelta) {
      // Crossed the anti-meridian: close segment and start new one
      lineSegments.push(currentSegment);
      currentSegment = [coords[i]];
    } else {
      currentSegment.push(coords[i]);
    }
  }

  if (currentSegment.length > 0) {
    lineSegments.push(currentSegment);
  }


  // RFC 7946 compliance: each LineString in a MultiLineString must have >= 2 coordinates
  const validSegments = lineSegments.filter((seg) => seg.length >= 2);

  return {
    type: 'Feature',
    id: baseFeature.id,
    properties: baseFeature.properties,
    geometry: {
      type: 'MultiLineString',
      coordinates: validSegments,
    },
  };
}

// ----------------------------------------------------------------------------
// Dynamic Filtering & Statistical Utilities
// ----------------------------------------------------------------------------

/**
 * High-performance O(N) multi-criteria photo filtering.
 */
export function filterPhotos(
  photos: PhotoRecord[],
  criteria: PhotoFilterCriteria
): PhotoRecord[] {
  const { category, timeRange, confidence, searchQuery } = criteria;
  const isAllCategory =
    category === undefined ||
    category === null ||
    category === 'Tutte' ||
    category === 'all';
  const timeStart = timeRange ? Math.min(timeRange[0], timeRange[1]) : -Infinity;
  const timeEnd = timeRange ? Math.max(timeRange[0], timeRange[1]) : Infinity;
  const isAllConfidence = !confidence || confidence === 'all';
  const query = searchQuery?.trim().toLowerCase();

  return photos.filter((p) => {
    if (!isAllCategory && p.category !== category) return false;
    if (p.timestampUtc < timeStart || p.timestampUtc > timeEnd) return false;
    if (!isAllConfidence && p.matchingConfidence !== confidence) return false;
    if (query) {
      const matchName = p.fileName.toLowerCase().includes(query);
      const matchStep = p.stepName?.toLowerCase().includes(query) ?? false;
      if (!matchName && !matchStep) return false;
    }
    return true;
  });
}

/**
 * Filters Polarsteps locations within a given UTC timestamp range.
 */
export function filterLocationsByTimeRange(
  locations: PolarstepsLocationPoint[],
  timeRange: [number, number]
): PolarstepsLocationPoint[] {
  const start = Math.min(timeRange[0], timeRange[1]);
  const end = Math.max(timeRange[0], timeRange[1]);
  return locations.filter((loc) => loc.time >= start && loc.time <= end);
}

/**
 * Computes photo counts grouped by category, including 'Tutte' total.
 */
export function computeCategoryCounts(photos: PhotoRecord[]): {
  counts: Record<string, number>;
  categories: string[];
  totalCount: number;
} {
  const counts: Record<string, number> = {};
  let totalCount = 0;

  for (let i = 0; i < photos.length; i++) {
    const cat = photos[i].category || 'Altro';
    counts[cat] = (counts[cat] || 0) + 1;
    totalCount++;
  }

  const categories = Object.keys(counts).sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
  counts['Tutte'] = totalCount;

  return {
    counts,
    categories,
    totalCount,
  };
}

/**
 * Computes overall trip minimum and maximum timestamp bounds.
 */
export function computeTripTimeBounds(
  locations: PolarstepsLocationPoint[],
  photos?: PhotoRecord[]
): [number, number] {
  let minTime = Infinity;
  let maxTime = -Infinity;

  if (locations && locations.length > 0) {
    for (let i = 0; i < locations.length; i++) {
      const t = locations[i].time;
      if (t < minTime) minTime = t;
      if (t > maxTime) maxTime = t;
    }
  }

  if (photos && photos.length > 0) {
    for (let i = 0; i < photos.length; i++) {
      const t = photos[i].timestampUtc;
      if (t < minTime) minTime = t;
      if (t > maxTime) maxTime = t;
    }
  }

  if (minTime === Infinity || maxTime === -Infinity) {
    const now = Math.floor(Date.now() / 1000);
    return [now, now];
  }

  return [minTime, maxTime];
}

/**
 * Finds the Polarsteps step nearest to a given photo timestamp.
 */
export function findNearestStep(
  timestampUtc: number,
  steps: PolarstepsStep[]
): PolarstepsStep | undefined {
  if (!steps || steps.length === 0) return undefined;

  // 1. Direct step timeframe containment
  for (let i = 0; i < steps.length; i++) {
    const s = steps[i];
    const end = s.end_time || s.start_time + 86400;
    if (timestampUtc >= s.start_time && timestampUtc <= end) {
      return s;
    }
  }

  // 2. Nearest start_time fallback
  let nearest = steps[0];
  let minDiff = Math.abs(timestampUtc - steps[0].start_time);

  for (let i = 1; i < steps.length; i++) {
    const diff = Math.abs(timestampUtc - steps[i].start_time);
    if (diff < minDiff) {
      minDiff = diff;
      nearest = steps[i];
    }
  }

  return nearest;
}

// ----------------------------------------------------------------------------
// Convenience & Test Compatibility Wrappers
// ----------------------------------------------------------------------------

export const locationsToRouteLineString = locationsToRouteFeature;

export function filterPhotosByCategory(geojson: PhotoGeoJSON, category: string): PhotoGeoJSON {
  if (!category || category === 'all' || category === 'Tutte') {
    return geojson;
  }
  return {
    ...geojson,
    features: geojson.features.filter((f) => f.properties.category === category),
  };
}

export function filterPhotosByTimeRange(
  geojson: PhotoGeoJSON,
  startUtc: number,
  endUtc: number
): PhotoGeoJSON {
  return {
    ...geojson,
    features: geojson.features.filter(
      (f) => f.properties.timestamp >= startUtc && f.properties.timestamp <= endUtc
    ),
  };
}

export function filterRouteByTimeRange(
  locations: PolarstepsLocationPoint[],
  startUtc: number,
  endUtc: number
): RouteLineFeature {
  return locationsToRouteFeature(locations, { timeRange: [startUtc, endUtc] });
}

// ----------------------------------------------------------------------------
// Phase 2 Video & Media GeoJSON Extensions
// ----------------------------------------------------------------------------

export interface MediaFeatureProperties extends PhotoFeatureProperties {
  mediaType: 'photo' | 'video';
  duration?: number;          // Duration in seconds
  durationFormatted?: string; // e.g. "0:45", "1:20"
  videoUrl?: string;          // Resolved preview video stream URL
  codec?: string;
}

export function formatVideoDuration(seconds: number): string {
  if (!seconds || seconds <= 0) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}


